/**
 * RecipeDetail Component
 *
 * Displays full recipe details including ingredients and step-by-step instructions.
 * Provides edit, delete, and cooking mode functionality.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useRecipeStore } from '../../stores/recipeStore';
import { useAuthStore } from '../../stores/authStore';
import './recipes.css';

interface RecipeDetailProps {
  recipeId: number;
}

export function RecipeDetail({ recipeId }: RecipeDetailProps) {
  const { t } = useTranslation(['recipes', 'common']);
  const { currentRecipe, fetchRecipe, deleteRecipe, setCurrentRecipe, isLoading, error } = useRecipeStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [servingsCount, setServingsCount] = useState<number | null>(null);

  useEffect(() => {
    if (recipeId) {
      setCurrentRecipe(null);
      setServingsCount(null);
      fetchRecipe(recipeId);
    }
  }, [recipeId, fetchRecipe, setCurrentRecipe]);

  useEffect(() => {
    if (currentRecipe && servingsCount === null) {
      setServingsCount(currentRecipe.servings);
    }
  }, [currentRecipe, servingsCount]);

  const handleDelete = async () => {
    if (!currentRecipe) return;

    try {
      await deleteRecipe(currentRecipe.id);
      // Redirect to recipes list after successful deletion
      navigate('/recipes');
    } catch (error) {
      console.error('Failed to delete recipe:', error);
    }
  };

  const handleEdit = () => {
    if (currentRecipe) {
      navigate(`/recipes/${currentRecipe.id}/edit`);
    }
  };

  const handleStartCooking = () => {
    if (currentRecipe) {
      navigate(`/recipes/${currentRecipe.id}/cook`);
    }
  };

  // If instructions is plain text (no HTML tags), convert newlines to <p> tags
  // so legacy recipes display correctly after the rich-text migration.
  // Also handles JSON array strings from older data format.
  const sanitizeInstructions = (instructions: string): string => {
    // Detect JSON array: ["step1", "step2", ...]
    const trimmed = instructions.trim();
    if (trimmed.startsWith('[')) {
      try {
        const steps = JSON.parse(trimmed);
        if (Array.isArray(steps)) {
          const html = '<ol>' + steps.map((s: unknown) => `<li>${String(s)}</li>`).join('') + '</ol>';
          return DOMPurify.sanitize(html, { ALLOWED_TAGS: ['p', 'ol', 'ul', 'li', 'strong', 'em', 'br'] });
        }
      } catch {
        // not valid JSON, fall through to plain-text handling
      }
    }

    const isHtml = /<[a-z][\s\S]*>/i.test(instructions);
    const html = isHtml
      ? instructions
      : instructions
          .split(/\n+/)
          .map(line => line.trim())
          .filter(line => line.length > 0)
          .map(line => `<p>${line}</p>`)
          .join('');
    return DOMPurify.sanitize(html, { ALLOWED_TAGS: ['p', 'ol', 'ul', 'li', 'strong', 'em', 'br'] });
  };

  if (error) {
    return (
      <div className="recipe-container">
        <div className="error-message" role="alert">
          {error}
        </div>
        <a href="/recipes" className="btn-secondary" style={{ marginTop: '1rem' }}>
          {t('backToRecipes')}
        </a>
      </div>
    );
  }

  if (isLoading || !currentRecipe || !currentRecipe.instructions) {
    return (
      <div className="recipe-container">
        <div className="loading-state" role="status" aria-live="polite">
          <p>{t('loadingRecipe')}</p>
        </div>
      </div>
    );
  }

  const isOwner = user && currentRecipe.user_id === user.id;
  const totalTime = (currentRecipe.prep_time || 0) + (currentRecipe.cook_time || 0);
  const instructionsHtml = sanitizeInstructions(currentRecipe.instructions);

  const activeServings = servingsCount ?? currentRecipe.servings;
  const scaleFactor = activeServings / currentRecipe.servings;

  const formatQuantity = (qty: number): string => {
    const scaled = qty * scaleFactor;
    // Round to 2 decimal places, remove trailing zeros
    return parseFloat(scaled.toFixed(2)).toString();
  };

  return (
    <div className="recipe-container">
      {/* Header Section */}
      <div className="recipe-detail-header">
        <div className="recipe-detail-title-section">
          <h1>{currentRecipe.title}</h1>
          {currentRecipe.description && (
            <p className="recipe-detail-description">{currentRecipe.description}</p>
          )}
        </div>

        {/* Meta Information */}
        <div className="recipe-detail-meta" aria-label="Recipe information">
          {currentRecipe.prep_time && (
            <div className="recipe-meta-item">
              <span className="meta-icon" aria-hidden="true">⏱</span>
              <div>
                <div className="meta-label">{t('prepTime')}</div>
                <div className="meta-value">{t('min', { time: currentRecipe.prep_time })}</div>
              </div>
            </div>
          )}
          {currentRecipe.cook_time && (
            <div className="recipe-meta-item">
              <span className="meta-icon" aria-hidden="true">🔥</span>
              <div>
                <div className="meta-label">{t('cookTime')}</div>
                <div className="meta-value">{t('min', { time: currentRecipe.cook_time })}</div>
              </div>
            </div>
          )}
          {totalTime > 0 && (
            <div className="recipe-meta-item">
              <span className="meta-icon" aria-hidden="true">⏲</span>
              <div>
                <div className="meta-label">{t('totalTime')}</div>
                <div className="meta-value">{t('min', { time: totalTime })}</div>
              </div>
            </div>
          )}
          <div className="recipe-meta-item">
            <span className="meta-icon" aria-hidden="true">🍽</span>
            <div>
              <div className="meta-label">{t('servingsLabel')}</div>
              <div className="servings-control">
                <button
                  className="servings-btn"
                  onClick={() => setServingsCount(Math.max(1, activeServings - 1))}
                  aria-label={t('decreaseServings')}
                  disabled={activeServings <= 1}
                >
                  −
                </button>
                <span className="meta-value servings-value">{activeServings}</span>
                <button
                  className="servings-btn"
                  onClick={() => setServingsCount(activeServings + 1)}
                  aria-label={t('increaseServings')}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="recipe-detail-actions">
        <button
          onClick={handleStartCooking}
          className="btn-primary"
          aria-label="Start cooking mode"
        >
          {'👨‍🍳 '}{t('startCooking')}
        </button>
        {isOwner && (
          <>
            <button
              onClick={handleEdit}
              className="btn-secondary"
              aria-label="Edit recipe"
            >
              {'✏️ '}{t('common:edit')}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn-danger"
              aria-label="Delete recipe"
            >
              {'🗑 '}{t('common:delete')}
            </button>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          className="delete-confirm-dialog"
          role="alertdialog"
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <p id="delete-dialog-description">
            {t('deleteConfirm')}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button onClick={handleDelete} className="btn-danger">
              {t('yesDelete')}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="btn-secondary"
            >
              {t('common:cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Nutrients per serving */}
      {currentRecipe.nutrients_per_serving && (
        <section className="recipe-nutrients-section" aria-label={t('nutrientsPerServing')}>
          <h2 className="section-title">{t('nutrientsPerServing')}</h2>
          <div className="nutrients-grid">
            {currentRecipe.nutrients_per_serving.calories != null && (
              <div className="nutrient-item nutrient-calories">
                <span className="nutrient-value">
                  {(currentRecipe.nutrients_per_serving.calories * scaleFactor).toFixed(1)}
                </span>
                <span className="nutrient-label">{t('calories')}</span>
                <span className="nutrient-unit">{t('kcal')}</span>
              </div>
            )}
            {currentRecipe.nutrients_per_serving.proteins != null && (
              <div className="nutrient-item">
                <span className="nutrient-value">
                  {(currentRecipe.nutrients_per_serving.proteins * scaleFactor).toFixed(1)}
                </span>
                <span className="nutrient-label">{t('proteins')}</span>
                <span className="nutrient-unit">{t('grams')}</span>
              </div>
            )}
            {currentRecipe.nutrients_per_serving.fats != null && (
              <div className="nutrient-item">
                <span className="nutrient-value">
                  {(currentRecipe.nutrients_per_serving.fats * scaleFactor).toFixed(1)}
                </span>
                <span className="nutrient-label">{t('fats')}</span>
                <span className="nutrient-unit">{t('grams')}</span>
              </div>
            )}
            {currentRecipe.nutrients_per_serving.carbohydrates != null && (
              <div className="nutrient-item">
                <span className="nutrient-value">
                  {(currentRecipe.nutrients_per_serving.carbohydrates * scaleFactor).toFixed(1)}
                </span>
                <span className="nutrient-label">{t('carbohydrates')}</span>
                <span className="nutrient-unit">{t('grams')}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Main Content */}
      <div className="recipe-detail-content">
        {/* Ingredients Section */}
        <section className="recipe-detail-section">
          <h2 className="section-title">{t('ingredients')}</h2>
          {currentRecipe.recipe_ingredients && currentRecipe.recipe_ingredients.length > 0 ? (
            <ul className="ingredients-list">
              {currentRecipe.recipe_ingredients.map((ri) => (
                <li key={ri.id} className="ingredient-item">
                  <span className="ingredient-quantity">
                    {formatQuantity(ri.quantity)} {ri.unit}
                  </span>
                  <span className="ingredient-name">{ri.ingredient.name}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="empty-message">{t('noIngredients')}</p>
          )}
        </section>

        {/* Instructions Section */}
        <section className="recipe-detail-section">
          <h2 className="section-title">{t('instructions')}</h2>
          <div
            className="instructions-html"
            dangerouslySetInnerHTML={{ __html: instructionsHtml }}
          />
        </section>
      </div>

      {/* Back to Recipes Link */}
      <div style={{ marginTop: '2rem' }}>
        <a href="/recipes" className="btn-secondary">
          {'← '}{t('backToRecipes')}
        </a>
      </div>
    </div>
  );
}

export default RecipeDetail;
